import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNews, getNewsArchiveIndex, trackVisit } from '../lib/api';
import { NewsCardSmall } from '../components/NewsCard';
import { Loader2, ArrowLeft, Archive, Calendar } from 'lucide-react';

const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function ArchivePage() {
  const nav = useNavigate();
  const [index, setIndex] = useState([]);
  const [selected, setSelected] = useState(null); // {year,month}
  const [news, setNews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingIdx, setLoadingIdx] = useState(true);
  const [loadingNews, setLoadingNews] = useState(false);

  useEffect(() => { trackVisit('/archivo'); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getNewsArchiveIndex();
        setIndex(data);
        if (data.length > 0) {
          const [y, m] = data[0].year_month.split('-');
          setSelected({ year: +y, month: +m });
        }
      } finally { setLoadingIdx(false); }
    })();
  }, []);

  const loadNews = useCallback(async () => {
    if (!selected) return;
    setLoadingNews(true);
    try {
      const { data } = await getNews(null, 1, '', selected.year, selected.month);
      setNews(data.news); setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoadingNews(false); }
  }, [selected]);

  useEffect(() => { loadNews(); }, [loadNews]);

  // Group index by year for sidebar
  const byYear = {};
  index.forEach(r => {
    const [y, m] = r.year_month.split('-');
    (byYear[y] = byYear[y] || []).push({ year: +y, month: +m, count: r.count });
  });
  const years = Object.keys(byYear).sort((a, b) => b - a);

  return (
    <div data-testid="archive-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6">
        <ArrowLeft className="h-3 w-3" /> VOLVER
      </button>

      <div className="mb-8 pb-4 border-b-4 border-[#0B1F40] flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15] flex items-center gap-2">
            <Archive className="h-3 w-3" /> ARCHIVO
          </span>
          <h1 className="text-4xl sm:text-5xl tracking-tighter text-[#0B1F40] leading-none" style={{ fontFamily: 'Archivo Black' }}>HEMEROTECA</h1>
        </div>
        {selected && (
          <div className="text-right">
            <p className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0B1F40]/40">MOSTRANDO</p>
            <p className="text-xl font-black text-[#E50B15]" style={{ fontFamily: 'Archivo Black' }}>
              {MONTHS_ES[selected.month - 1]} {selected.year}
            </p>
            <p className="text-[10px] text-[#0B1F40]/50 font-bold">{total} NOTICIA{total === 1 ? '' : 'S'}</p>
          </div>
        )}
      </div>

      {loadingIdx ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>
      ) : index.length === 0 ? (
        <div className="text-center py-16"><p className="text-sm text-[#0B1F40]/40 font-bold">No hay noticias archivadas todavía</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          {/* Sidebar */}
          <aside data-testid="archive-sidebar" className="bg-white border-2 border-[#0B1F40] p-4 h-fit lg:sticky lg:top-24" style={{ boxShadow: '4px 4px 0 #E50B15' }}>
            <h3 className="text-[10px] font-black tracking-[0.25em] uppercase text-[#0B1F40]/60 mb-3 flex items-center gap-1.5">
              <Calendar className="h-3 w-3" /> POR FECHA
            </h3>
            <div className="space-y-4">
              {years.map(y => (
                <div key={y}>
                  <p className="text-lg font-black text-[#0B1F40] mb-1" style={{ fontFamily: 'Archivo Black' }}>{y}</p>
                  <div className="space-y-0.5">
                    {byYear[y].sort((a, b) => b.month - a.month).map(({ year, month, count }) => {
                      const isSel = selected?.year === year && selected?.month === month;
                      return (
                        <button key={`${year}-${month}`} data-testid={`archive-${year}-${month}`}
                          onClick={() => setSelected({ year, month })}
                          className={`w-full flex items-center justify-between px-2 py-1.5 text-[11px] font-bold transition-colors ${isSel ? 'bg-[#E50B15] text-white' : 'text-[#0B1F40]/70 hover:bg-[#F4F5F7]'}`}>
                          <span>{MONTHS_ES[month - 1]}</span>
                          <span className={`text-[9px] ${isSel ? 'text-white/70' : 'text-[#0B1F40]/30'}`}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* News grid */}
          <div>
            {loadingNews ? (
              <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>
            ) : news.length === 0 ? (
              <p className="text-center py-20 text-sm text-[#0B1F40]/40 font-bold">No hay noticias en este período</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {news.map(n => <NewsCardSmall key={n.id} news={n} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
